import React, { useCallback, useEffect } from "react";
import { Transition, Dialog } from "@headlessui/react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getData, putData } from "actions";
import {
  API_URL_department,
  API_URL_helpTopic,
  API_URL_slaplan,
  API_URL_ticket,
  API_URL_ticketPriority,
  API_URL_ticketSource,
} from "constants/api";
import { slaplanReducers } from "redux/slaplanSlice";
import { helpTopicReducers } from "redux/helpTopicSlice";
import { ticketSourceReducers } from "redux/ticketSourceSlice";
import { ticketPriorityReducers } from "redux/ticketPrioritySlice";
import { ticketReducers } from "redux/ticketSlice";
import { capitalFirstLetter } from "utils/capitalFirstLetter";
import { departmentReducers } from "redux/departmentSlice";

const ModalEditTicket = ({ isOpen, onClose, item, callback }) => {
  const cancelButtonRef = React.useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    getSlaplanResult,
    getHelpTopicResult,
    getTicketSourceResult,
    getTicketPriorityResult,
    getDepartmentResult,
  } = useSelector((state) => ({
    getSlaplanResult: state.slaplan.getSlaplanResult,
    getHelpTopicResult: state.helpTopic.getHelpTopicResult,
    getTicketSourceResult: state.ticketSource.getTicketSourceResult,
    getTicketPriorityResult: state.ticketPriority.getTicketPriorityResult,
    getDepartmentResult: state.department.getDepartmentResult,
  }));

  const getSlaplan = useCallback(
    async (params) => {
      getData(
        API_URL_slaplan,
        params,
        { dispatch, redux: slaplanReducers },
        "GET_SLAPLAN"
      );
    },
    [dispatch]
  );

  const getDepartment = useCallback(
    async (params) => {
      getData(
        API_URL_department,
        params,
        { dispatch, redux: departmentReducers },
        "GET_DEPARTMENT"
      );
    },
    [dispatch]
  );

  const getHelptopic = useCallback(
    async (params) => {
      getData(
        API_URL_helpTopic,
        params,
        { dispatch, redux: helpTopicReducers },
        "GET_HELPTOPIC"
      );
    },
    [dispatch]
  );

  const getTicketsource = useCallback(
    async (params) => {
      getData(
        API_URL_ticketSource,
        params,
        { dispatch, redux: ticketSourceReducers },
        "GET_TICKETSOURCE"
      );
    },
    [dispatch]
  );

  const getTicketpriority = useCallback(
    async (params) => {
      getData(
        API_URL_ticketPriority,
        params,
        { dispatch, redux: ticketPriorityReducers },
        "GET_TICKETPRIORITY"
      );
    },
    [dispatch]
  );

  const formik = useFormik({
    initialValues: {
      title: "",
      sla: "",
      source: "",
      priority: "",
      dept: "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required"),
      sla: Yup.string().required("Slaplan is required"),
      source: Yup.string().required("Ticket Source is required"),
      priority: Yup.string().required("Priority is required"),
    }),
    onSubmit: (values) => {
      let formData = new FormData();
      formData.append("title", values.title);
      formData.append("sla", values.sla);
      formData.append("source", values.source);
      formData.append("priority", values.priority);
      formData.append("dept", values.dept);
      putData(
        `${API_URL_ticket}${item.id}/`,
        formData,
        { dispatch, redux: ticketReducers },
        "ADD_TICKET",
        () => callback()
      );

      // Tutup modal
      onClose();
    },
  });

  useEffect(() => {
    const params = `?limit=${9999999}`;
    getTicketpriority(params);
    getTicketsource(params);
    getSlaplan(params);
    getHelptopic(params);
    getDepartment(params);
  }, []);

  useEffect(() => {
    if (item) {
      formik.setFieldValue("title", item.title);
      formik.setFieldValue("sla", item.sla);
      formik.setFieldValue("source", item.source);
      formik.setFieldValue("help_topic", item.help_topic);
      formik.setFieldValue("priority", item.priority);
      formik.setFieldValue("dept", item.dept);
    }
  }, [item]);

  return (
    <Transition.Root show={isOpen} as={React.Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-al w-full lg:w-1/2">
                <form onSubmit={formik.handleSubmit}>
                  <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                    <div className="mt-3 sm:ml-4 sm:mt-0">
                      <div className="flex justify-between">
                        <Dialog.Title
                          as="h3"
                          className="text-2xl font-semibold leading-6 text-gray-900"
                        >
                          {`Edit [${item && item.ticket_number}]`}
                        </Dialog.Title>
                        <button
                          type="button"
                          className=" bg-gray-300 px-3 py-2 text-sm font-semibold text-black shadow-sm hover:bg-gray-400"
                          onClick={onClose}
                        >
                          X
                        </button>
                      </div>
                      <hr className="my-2" />
                      <div className="px-6 py-1">
                        <label htmlFor="link" className="block mb-1">
                          Title <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="title"
                          name="title"
                          value={formik.values.title}
                          onChange={formik.handleChange}
                          className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                        />
                        {formik.touched.title && formik.errors.title && (
                          <div className="text-red-500 dark:text-red-300 text-xs">
                            {formik.errors.title}
                          </div>
                        )}
                      </div>

                      <div className="px-6 py-1">
                        <label htmlFor="link" className="block mb-1">
                          SLA plan <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="sla"
                          name="sla"
                          value={formik.values.sla}
                          onChange={formik.handleChange}
                          className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                        >
                          <option value="">Select a Slaplan</option>
                          {getSlaplanResult.results &&
                            getSlaplanResult.results.map((item, index) => (
                              <option key={index} value={item.id}>
                                {item.name}
                              </option>
                            ))}
                        </select>
                        {formik.touched.sla && formik.errors.sla && (
                          <div className="text-red-500 dark:text-red-300 text-xs">
                            {formik.errors.sla}
                          </div>
                        )}
                      </div>

                      <div className="px-6 py-1">
                        <label htmlFor="link" className="block mb-1">
                          Ticket Departement{" "}
                        </label>
                        <select
                          id="dept"
                          name="dept"
                          value={formik.values.dept}
                          onChange={formik.handleChange}
                          className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                        >
                          <option value="">Select a Ticket Departement</option>
                          {getDepartmentResult.results &&
                            getDepartmentResult.results.map((item, index) => (
                              <option key={index} value={item.id}>
                                {capitalFirstLetter(item.name)}
                              </option>
                            ))}
                        </select>
                        {formik.touched.dept && formik.errors.dept && (
                          <div className="text-red-500 dark:text-red-300 text-xs">
                            {formik.errors.dept}
                          </div>
                        )}
                      </div>

                      <div className="px-6 py-1">
                        <label htmlFor="link" className="block mb-1">
                          Ticket Source <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="source"
                          name="source"
                          value={formik.values.source}
                          onChange={formik.handleChange}
                          className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                        >
                          <option value="">Select a Ticket Source</option>
                          {getTicketSourceResult.results &&
                            getTicketSourceResult.results.map((item, index) => (
                              <option key={index} value={item.id}>
                                {capitalFirstLetter(item.name)}
                              </option>
                            ))}
                        </select>
                        {formik.touched.source && formik.errors.source && (
                          <div className="text-red-500 dark:text-red-300 text-xs">
                            {formik.errors.source}
                          </div>
                        )}
                      </div>

                      <div className="px-6 py-1">
                        <label htmlFor="link" className="block mb-1">
                          Priority
                          <span className="text-red-500">*</span>
                        </label>
                        <select
                          id="priority"
                          name="priority"
                          value={formik.values.priority}
                          onChange={formik.handleChange}
                          className="block w-full rounded-md border border-gray-500 bg-white py-2 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                        >
                          <option value="">Select a Priority</option>
                          {getTicketPriorityResult.results &&
                            getTicketPriorityResult.results.map(
                              (item, index) => (
                                <option key={index} value={item.id}>
                                  {item.priority}
                                </option>
                              )
                            )}
                        </select>
                        {formik.touched.priority && formik.errors.priority && (
                          <div className="text-red-500 dark:text-red-300 text-xs">
                            {formik.errors.priority}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-50 flex justify-between px-4 py-3 sm:px-6">
                    <button
                      type="button"
                      className="inline-flex justify-center rounded-md bg-gray-300 px-3 py-2 text-sm font-semibold text-black shadow-sm hover:bg-gray-400"
                      onClick={onClose}
                    >
                      Close
                    </button>
                    <button
                      type="submit"
                      className="inline-flex justify-center rounded-md bg-sky-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-600"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default ModalEditTicket;
